<div class="dynamic-search-dialog">
  <h2 mat-dialog-title>🔍 Advanced Property Search</h2>

  <mat-dialog-content>
    <form [formGroup]="searchForm" (ngSubmit)="onSearch()" class="search-form">
      <mat-form-field appearance="outline" class="search-type-field">
        <mat-label>Search Type</mat-label>
        <mat-select formControlName="searchType">
          <mat-option *ngFor="let type of searchTypes" [value]="type.value">
            {{ type.label }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline" class="search-input-field">
        <mat-label>Search Query</mat-label>
        <input matInput #searchInput id="search-input" formControlName="searchQuery" placeholder="Enter property unit number (e.g., #1A/4, #2B/12)" (keyup.enter)="onSearch()">
        <button mat-icon-button matSuffix type="submit" [disabled]="!searchForm.valid || isLoading">
          <mat-icon>search</mat-icon>
        </button>
      </mat-form-field>

      <button mat-raised-button color="primary" class="search-button" type="submit" [disabled]="!searchForm.valid || isLoading">
        <mat-icon *ngIf="isLoading">hourglass_empty</mat-icon>
        <mat-icon *ngIf="!isLoading">search</mat-icon>
        Find Property
      </button>
    </form>

    <!-- Loading Indicator -->
    <div *ngIf="isLoading" class="loading-container">
      <mat-spinner diameter="40"></mat-spinner>
      <p>Searching...</p>
    </div>

    <!-- Error Display -->
    <div *ngIf="error && !isLoading" class="error-container">
      <mat-icon color="warn">{{ getErrorIcon() }}</mat-icon>
      <div class="error-content">
        <h4>{{ getErrorTitle() }}</h4>
        <p>{{ error }}</p>
        <button mat-stroked-button color="primary" (click)="onRetry()" *ngIf="canRetry()">
          <mat-icon>refresh</mat-icon>
          Try Again
        </button>
      </div>
    </div>

    <!-- Search Results -->
    <div *ngIf="searchResult && !isLoading" class="results-container">
      <!-- Unit Details Card -->
      <div class="unit-details-card">
        <h3>Unit Details</h3>
        <div class="unit-info">
          <div class="unit-info-row">
            <span class="label">Property:</span>
            <span class="value">{{ searchResult.unit_details.property_name }}</span>
          </div>
          <div class="unit-info-row">
            <span class="label">Unit No:</span>
            <span class="value">{{ searchResult.unit_details.unit_no }}</span>
          </div>
          <div class="unit-info-row">
            <span class="label">Property Unit No:</span>
            <span class="value">{{ searchResult.unit_details.property_unit_no }}</span>
          </div>
          <div class="unit-info-row">
            <span class="label">Type:</span>
            <span class="value">{{ searchResult.unit_details.type }}</span>
          </div>
          <div class="unit-info-row">
            <span class="label">Rent:</span>
            <span class="value">{{ searchResult.unit_details.rent }}</span>
          </div>
          <div class="unit-info-row">
            <span class="label">Deposit:</span>
            <span class="value">{{ searchResult.unit_details.deposit }}</span>
          </div>
          <div class="unit-info-row">
            <span class="label">Status:</span>
            <mat-chip [ngClass]="searchResult.unit_details.availability_status.toLowerCase()" color="primary" selected>
              {{ searchResult.unit_details.availability_status }}
            </mat-chip>
          </div>
          <div class="unit-info-row" *ngIf="searchResult.unit_details.vacant_date">
            <span class="label">Vacant Date:</span>
            <span class="value">{{ searchResult.unit_details.vacant_date | date }}</span>
          </div>
        </div>
      </div>

      <!-- Contracts Carousel -->
      <div class="contracts-section" *ngIf="searchResult.total_contracts > 0">
        <h3>Contracts ({{ searchResult.total_contracts }})</h3>
        <div class="contracts-carousel-container">
          <div class="carousel-header">
            <div class="status-title">
              <mat-icon>{{ getStatusIcon(currentContractStatus) }}</mat-icon>
              {{ currentContractStatus | titlecase }} Contracts ({{ getCurrentContracts().length }})
            </div>
            <div class="carousel-controls">
              <div class="status-indicator">
                <div
                  *ngFor="let status of availableStatuses; let i = index"
                  class="dot"
                  [class.active]="i === currentStatusIndex"
                  (click)="goToStatus(i)"
                  [title]="status | titlecase">
                </div>
              </div>
              <button
                class="nav-button"
                (click)="previousStatus()"
                [disabled]="currentStatusIndex === 0">
                <mat-icon>chevron_left</mat-icon>
              </button>
              <button
                class="nav-button"
                (click)="nextStatus()"
                [disabled]="currentStatusIndex === availableStatuses.length - 1">
                <mat-icon>chevron_right</mat-icon>
              </button>
            </div>
          </div>

          <div class="carousel-content">
            <div class="contract-items" *ngIf="getCurrentContracts().length > 0">
              <div class="contract-item" *ngFor="let contract of getCurrentContracts()">
                <div class="contract-header">
                  <span class="contract-no">#{{ contract.contract_no }}</span>
                  <span class="contract-status" [ngClass]="contract.status.toLowerCase()">
                    {{ contract.status }}
                  </span>
                </div>
                <div class="contract-details">
                  <div class="detail-item">
                    <span class="label">Tenant</span>
                    <span class="value">{{ contract.tenant_name }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">Start Date</span>
                    <span class="value">{{ contract.start_date | date:'shortDate' }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">End Date</span>
                    <span class="value">{{ contract.end_date | date:'shortDate' }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">Rent</span>
                    <span class="value">{{ contract.rent | currency }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">Deposit</span>
                    <span class="value">{{ contract.deposit | currency }}</span>
                  </div>
                  <div class="detail-item" *ngIf="contract.total_invoices !== undefined">
                    <span class="label">Invoices</span>
                    <span class="value">{{ contract.total_invoices }}</span>
                  </div>
                  <div class="detail-item" *ngIf="contract.total_cheques_received !== undefined">
                    <span class="label">Cheques</span>
                    <span class="value">{{ contract.total_cheques_received }}</span>
                  </div>
                  <div class="detail-item" *ngIf="contract.cheques_received_date">
                    <span class="label">Cheques Received</span>
                    <span class="value">{{ contract.cheques_received_date | date:'shortDate' }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="no-contracts" *ngIf="getCurrentContracts().length === 0">
              No {{ currentContractStatus }} contracts found
            </div>
          </div>
        </div>
      </div>

      <!-- Booking Details -->
      <div class="booking-details-section" *ngIf="searchResult.booking_details?.length > 0">
        <h3>📅 Booking Details</h3>
        <div class="booking-items">
          <div class="booking-item" *ngFor="let booking of searchResult.booking_details">
            <div class="booking-header">
              <div class="booking-status" [ngClass]="booking.status?.toLowerCase()">
                <mat-icon>{{ getBookingStatusIcon(booking.status) }}</mat-icon>
                {{ booking.status }}
              </div>
              <div class="booking-date">
                <mat-icon>event</mat-icon>
                {{ booking.booking_date | date:'mediumDate' }}
              </div>
            </div>

            <div class="booking-content">
              <div class="booking-section">
                <h4>📋 Booking Information</h4>
                <div class="booking-grid">
                  <div class="booking-detail">
                    <span class="label">Sales Person</span>
                    <span class="value">{{ booking.sales_person }}</span>
                  </div>
                  <div class="booking-detail">
                    <span class="label">Contract Type</span>
                    <span class="value">{{ booking.contract_type }}</span>
                  </div>
                  <div class="booking-detail">
                    <span class="label">Payment Mode</span>
                    <span class="value">{{ booking.payment_mode }}</span>
                  </div>
                  <div class="booking-detail" *ngIf="booking.key_hand_over_date">
                    <span class="label">Key Handover</span>
                    <span class="value">{{ booking.key_hand_over_date | date:'shortDate' }}</span>
                  </div>
                </div>
              </div>

              <div class="booking-section">
                <h4>💰 Financial Details</h4>
                <div class="financial-grid">
                  <div class="financial-item advance">
                    <mat-icon>account_balance_wallet</mat-icon>
                    <div class="financial-info">
                      <span class="label">Advance Amount</span>
                      <span class="amount">{{ booking.advance_amount | currency }}</span>
                    </div>
                  </div>
                  <div class="financial-item deposit">
                    <mat-icon>savings</mat-icon>
                    <div class="financial-info">
                      <span class="label">Deposit Amount</span>
                      <span class="amount">{{ booking.deposit_amount | currency }}</span>
                    </div>
                  </div>
                  <div class="financial-item rent">
                    <mat-icon>home</mat-icon>
                    <div class="financial-info">
                      <span class="label">Rent Amount</span>
                      <span class="amount">{{ booking.rent_amount | currency }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="booking-section">
                <h4>📅 Duration</h4>
                <div class="duration-info">
                  <div class="date-range">
                    <div class="date-item">
                      <mat-icon>event_available</mat-icon>
                      <div>
                        <span class="label">Start Date</span>
                        <span class="date">{{ booking.start_date | date:'mediumDate' }}</span>
                      </div>
                    </div>
                    <div class="date-separator">
                      <mat-icon>arrow_forward</mat-icon>
                    </div>
                    <div class="date-item">
                      <mat-icon>event_busy</mat-icon>
                      <div>
                        <span class="label">End Date</span>
                        <span class="date">{{ booking.end_date | date:'mediumDate' }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="duration-summary">
                    <span class="duration-text">{{ calculateDuration(booking.start_date, booking.end_date) }}</span>
                  </div>
                </div>
              </div>

              <div class="booking-section" *ngIf="booking.comments">
                <h4>💬 Comments</h4>
                <div class="comments-box">
                  <p>{{ booking.comments }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Contracts Message -->
      <div class="no-contracts-message" *ngIf="searchResult.total_contracts === 0">
        <mat-icon>info</mat-icon>
        <p>No contracts found for this property unit.</p>
      </div>

    </div>
  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button mat-button (click)="onClear()" [disabled]="isLoading">Clear</button>
    <button mat-button (click)="onClose()">Close</button>
  </mat-dialog-actions>
</div>
